import React, { useState, useEffect, useCallback } from 'react';
import MainSidebar from 'components/MainSidebar';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../hooks/useRealtimeSubscription';
import SeasonTimeline from './components/SeasonTimeline';
import EventCard from './components/EventCard';
import SeasonLeaderboard from './components/SeasonLeaderboard';
import TournamentStatus from './components/TournamentStatus';
import VolatilityIndicator from './components/VolatilityIndicator';
import CosmeticRewards from './components/CosmeticRewards';

const SeasonalEventsPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState(null);
  const [events, setEvents] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [tournament, setTournament] = useState(null);
  const [economyState, setEconomyState] = useState(null);
  const [cosmeticRewards, setCosmeticRewards] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [realtimeStatus, setRealtimeStatus] = useState('idle');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch active season
      const { data: seasonData } = await supabase
        ?.from('seasons')
        ?.select('*')
        ?.eq('is_active', true)
        ?.limit(1)
        ?.maybeSingle();
      setSeason(seasonData);

      // Fetch active season events
      if (seasonData?.id) {
        const { data: eventsData } = await supabase
          ?.from('season_events')
          ?.select('*')
          ?.eq('season_id', seasonData?.id)
          ?.eq('is_active', true)
          ?.order('event_start', { ascending: true });
        setEvents(eventsData || []);

        // Fetch tournament
        const { data: tournamentData } = await supabase
          ?.from('faction_tournaments')
          ?.select('*')
          ?.eq('season_id', seasonData?.id)
          ?.eq('status', 'active')
          ?.limit(1)
          ?.maybeSingle();
        setTournament(tournamentData);

        // Fetch season leaderboard
        const { data: lbData } = await supabase
          ?.from('season_leaderboards')
          ?.select('*, user_profiles(username)')
          ?.eq('season_number', seasonData?.season_number)
          ?.order('final_net_worth', { ascending: false })
          ?.limit(50);
        setLeaderboard(lbData || []);
      }

      // Fetch economy state
      const { data: econData } = await supabase
        ?.from('economy_state')
        ?.select('*')
        ?.limit(1)
        ?.maybeSingle();
      setEconomyState(econData);

      // Fetch cosmetic rewards
      const { data: rewardsData } = await supabase
        ?.from('cosmetic_rewards')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('reward_tier', { ascending: true });
      setCosmeticRewards(rewardsData || []);
    } catch (err) {
      console.error('Seasonal events fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Real-time: refresh on economy_state changes
  const { status: rtStatus } = useRealtimeSubscription(
    'seasonal-economy',
    { table: 'economy_state', event: 'UPDATE' },
    useCallback(() => {
      supabase?.from('economy_state')?.select('*')?.limit(1)?.maybeSingle()
        ?.then(({ data }) => { if (data) setEconomyState(data); });
    }, [])
  );

  useEffect(() => { setRealtimeStatus(rtStatus); }, [rtStatus]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'events', label: 'Events', icon: 'Zap' },
    { id: 'leaderboard', label: 'Leaderboard', icon: 'Trophy' },
    { id: 'rewards', label: 'Rewards', icon: 'Gift' },
  ];

  return (
    <div className="flex h-screen bg-background text-foreground overflow-hidden">
      <MainSidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <Icon name="Calendar" size={20} className="text-accent" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Seasonal Events</h1>
              <p className="text-xs text-muted">4-week competitive season with global economic events</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs ${
              realtimeStatus === 'connected' ? 'text-success' : 'text-muted'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${
                realtimeStatus === 'connected' ? 'bg-success animate-pulse' : 'bg-muted'
              }`} />
              {realtimeStatus === 'connected' ? 'Live' : 'Offline'}
            </div>
            <button
              onClick={fetchData}
              className="p-2 rounded-lg bg-surface-700 hover:bg-surface-600 transition-colors"
              title="Refresh"
            >
              <Icon name="RefreshCw" size={16} className="text-muted" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4 border-b border-border bg-surface">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="lg:col-span-2">
                <SeasonTimeline season={season} loading={loading} />
              </div>
              <VolatilityIndicator economyState={economyState} loading={loading} />
              <TournamentStatus tournament={tournament} loading={loading} />
            </div>
          )}

          {activeTab === 'events' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-foreground">Active Market Events</h2>
                <span className="text-xs text-muted">{events?.length} active</span>
              </div>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(4)]?.map((_, i) => (
                    <div key={i} className="h-40 bg-surface-700 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : events?.length === 0 ? (
                <div className="text-center py-16 text-muted">
                  <Icon name="Calendar" size={48} className="mx-auto mb-3 opacity-30" />
                  <p className="text-base font-medium">No active events</p>
                  <p className="text-sm mt-1">Check back soon for upcoming market events</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {events?.map((event) => (
                    <EventCard key={event?.id} event={event} />
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'leaderboard' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <SeasonLeaderboard leaderboard={leaderboard} loading={loading} />
              </div>
              <div>
                <TournamentStatus tournament={tournament} loading={loading} />
              </div>
            </div>
          )}

          {activeTab === 'rewards' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <CosmeticRewards rewards={cosmeticRewards} loading={loading} />
              <div className="card p-6">
                <h3 className="text-base font-bold text-foreground flex items-center gap-2 mb-4">
                  <Icon name="Info" size={18} className="text-primary" />
                  Season Reset Info
                </h3>
                <div className="space-y-3">
                  {[
                    { icon: 'RotateCcw', title: 'Soft Reset', desc: 'Cash, portfolio, and businesses reset to starter values at season end' },
                    { icon: 'Shield', title: 'Preserved', desc: 'Achievements, cosmetics, prestige level, and total XP earned are kept' },
                    { icon: 'TrendingUp', title: 'Prestige', desc: 'Prestige level increments each season, unlocking exclusive bonuses' },
                    { icon: 'Trophy', title: 'Leaderboard Reset', desc: 'Rankings reset at season start; final standings determine reward tiers' },
                  ]?.map((item) => (
                    <div key={item?.title} className="flex items-start gap-3 p-3 bg-surface-700 rounded-lg">
                      <Icon name={item?.icon} size={16} className="text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{item?.title}</p>
                        <p className="text-xs text-muted mt-0.5">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeasonalEventsPage;
